import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from './ui/form';
import { PartnerSelector } from './PartnerSelector';

import { payerApi } from '../lib/api/payerApi';
import { CreatePayerRequest, PartnerDto } from '../lib/api/types';

// Validation schema for payer creation
const createPayerSchema = z.object({
  Name: z
    .string()
    .min(1, 'Name is required')
    .max(100, 'Name must be 100 characters or less'),
  // Optional: allow blank or valid email format
  EmailAddress: z
    .union([
      z
        .string()
        .email('Invalid email format')
        .max(500, 'Email must be 500 characters or less'),
      z.literal(''),
    ])
    .optional(),
  // Optional: allow blank or non-empty up to 100 chars
  ExternalId1: z
    .union([
      z.string().max(100, 'External ID 1 must be 100 characters or less'),
      z.literal(''),
    ])
    .optional(),
  ExternalId2: z
    .string()
    .max(50, 'External ID 2 must be 50 characters or less')
    .optional(),
  PartnerId: z
    .string()
    .min(1, 'Partner selection is required'),
  AuthToken: z
    .string()
    .min(1, 'Auth token is required'),
});

type CreatePayerFormData = z.infer<typeof createPayerSchema>;

interface CreatePayerFormProps {
  onSuccess?: (externalAccountId: string) => void;
}

export const CreatePayerForm: React.FC<CreatePayerFormProps> = ({ onSuccess }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedPartner, setSelectedPartner] = React.useState<PartnerDto | null>(null);

  const form = useForm<CreatePayerFormData>({
    resolver: zodResolver(createPayerSchema),
    defaultValues: {
      Name: '',
      EmailAddress: '',
      ExternalId1: '',
      ExternalId2: '',
      PartnerId: '',
      AuthToken: '',
    },
  });

  const createPayerMutation = useMutation({
    mutationFn: async (data: CreatePayerFormData) => {
      if (!selectedPartner?.billerId) {
        throw new Error('Selected partner is missing billerId');
      }
      const email = (data.EmailAddress ?? '').trim();
      const externalId1 = (data.ExternalId1 ?? '').trim();
      const request: CreatePayerRequest = {
        Name: data.Name,
        EmailAddress: email ? email : undefined,
        ExternalId1: externalId1 ? externalId1 : undefined,
        ExternalId2: data.ExternalId2 ? data.ExternalId2 : undefined,
      };
      return payerApi.createPayer(request, selectedPartner.billerId, data.AuthToken);
    },
    onSuccess: (resp) => {
      toast.success('Payer created successfully', {
        description: `ExternalAccountID: ${resp.ExternalAccountID}`,
      });
      form.reset();
      setSelectedPartner(null);
      onSuccess?.(resp.ExternalAccountID);
      // Refresh payers list
      queryClient.invalidateQueries({ queryKey: ['payers'] });
      navigate('/payers');
    },
    onError: (error: any) => {
      console.error('Error creating payer:', error);
      let errorMessage = 'Failed to create payer';

      if (error.response?.data?.ErrorMessage) {
        errorMessage = error.response.data.ErrorMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error('Failed to create payer', {
        description: errorMessage,
      });
    },
  });

  const onSubmit = (data: CreatePayerFormData) => {
    if (!selectedPartner) {
      toast.error('Please select a partner');
      return;
    }

    createPayerMutation.mutate(data);
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle>Create Payer</CardTitle>
        <CardDescription>
          Create a new payer using partner credentials
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Name */}
            <FormField
              control={form.control}
              name="Name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter payer name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Email */}
            <FormField
              control={form.control}
              name="EmailAddress"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email Address (optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter email address" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* ExternalId1 */}
            <FormField
              control={form.control}
              name="ExternalId1"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>External ID 1 (optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter External ID 1" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* ExternalId2 */}
            <FormField
              control={form.control}
              name="ExternalId2"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>External ID 2 (optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter External ID 2" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Partner Selection */}
            <FormField
              control={form.control}
              name="PartnerId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Partner</FormLabel>
                  <FormControl>
                    <PartnerSelector
                      selectedPartnerId={field.value}
                      onPartnerSelect={(partner) => {
                        field.onChange(partner?.partnerId || '');
                        setSelectedPartner(partner);
                      }}
                      placeholder="Select a partner"
                      required
                    />
                  </FormControl>
                  <FormDescription>
                    Select the partner whose credentials will authorize this request
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Auth Token */}
            <FormField
              control={form.control}
              name="AuthToken"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Auth Token</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="Enter partner auth token" {...field} />
                  </FormControl>
                  <FormDescription>
                    Used with the partner's billerId for Basic auth
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Submit Button */}
            <Button type="submit" className="w-full" disabled={createPayerMutation.isPending}>
              {createPayerMutation.isPending ? 'Creating...' : 'Create Payer'}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};
